import React, { useEffect, useMemo, useState } from "react"
import { capitalize } from "lodash"

import { api } from "~/utils/api"
import { DateFormatType, formatDate, formatRelativeDate, startOfTodayUTC } from "~/utils/datetime"

import { Card, CardContent, CardHeader } from "../reusable/Card"
import LoadingOverlay from "../reusable/LoadingOverlay"
import Select from "../reusable/Select"
import { Skeleton } from "../reusable/Skeleton"
import { Check, Info, Warning, WarningCircle } from "@phosphor-icons/react"
import { useAuth0 } from "@auth0/auth0-react"
import { useUser } from "~/providers/userProvider"
import { ValidationReportDisplay } from "./ValidationReportDisplay"

interface OrgValidationReportProps {
  orgId: string
}

export const validationStatusIcon = (result: string) => {
  switch (result) {
    case "ValidationResult.SUCCESS":
      return <Check className="h-6 w-6 text-green-500" />
    case "ValidationResult.WARNING":
      return <Warning className="h-6 w-6 text-orange-500" />
    case "ValidationResult.FAILURE":
      return <WarningCircle className="h-6 w-6 text-red-500" />
    case "ValidationResult.INFO":
      return <Info className="h-6 w-6 text-violet-500" />
    default:
      return null
  }
}

const OrgValidationReport: React.FC<OrgValidationReportProps> = ({ orgId }) => {
  const { user, isAuthenticated, isLoading } = useAuth0()
  const { permissions } = useUser()

  const [selectedTimestamp, setSelectedTimestamp] = useState<string | null>(null)

  const { data: validationTimestamps } = api.bigQuery.validationTimestamps.useQuery({
    orgId,
  })

  const { data: validationReport } = api.bigQuery.validationReport.useQuery(
    {
      orgId,
      timestamp: selectedTimestamp!,
    },
    { enabled: selectedTimestamp !== null },
  )

  const validationTimestampOptions = useMemo(() => {
    return validationTimestamps?.map((timestamp) => {
      const filteredResults = timestamp.results.filter(
        (result) => result.testResult !== "ValidationResult.SUCCESS",
      )
      return {
        label:
          capitalize(
            formatRelativeDate(
              new Date(timestamp.testTimestamp),
              startOfTodayUTC(),
              DateFormatType.WithTime,
            ),
          ) +
          (filteredResults.length > 0
            ? " (" +
              filteredResults
                .map(
                  (result) =>
                    capitalize(result.testResult.replace("ValidationResult.", "")) +
                    ": " +
                    result.resultCount,
                )
                .join(", ") +
              ")"
            : ""),
        value: timestamp,
      }
    })
  }, [validationTimestamps])

  useEffect(() => {
    if (validationTimestampOptions && validationTimestampOptions.length > 0) {
      setSelectedTimestamp(validationTimestampOptions[0]!.value.testTimestamp)
    }
  }, [validationTimestampOptions])

  if (!user && isLoading) {
    return <LoadingOverlay />
  }

  if (!isAuthenticated) {
    return <div className="bg-subtle p-4">Not authenticated</div>
  }

  if (!permissions.isStaff) {
    return <div className="bg-subtle p-4">User not allowed!</div>
  }

  if (!validationReport) {
    return <Skeleton className="h-[300px]" />
  }

  return (
    <Card>
      <CardHeader className="h-fit">
        <div className="flex flex-col">
          <h2 className="font-medium">Validation report</h2>
          {validationReport.length > 0 && (
            <p className="text-sm text-fg-subtle">
              Date:{" "}
              {formatDate(new Date(validationReport[0]!.testTimestamp), DateFormatType.WithTime)}
            </p>
          )}
        </div>

        <Select
          options={validationTimestampOptions}
          value={validationTimestampOptions?.find(
            (option) => option.value.testTimestamp === selectedTimestamp,
          )}
          onChange={(e: any) => setSelectedTimestamp(e.value.testTimestamp)}
        />
      </CardHeader>
      <CardContent className="flex flex-col gap-2.5">
        {validationReport.map((report, index) => (
          <ValidationReportDisplay key={index} report={report} />
        ))}
      </CardContent>
    </Card>
  )
}

export default OrgValidationReport
